/**
 * useAction / ActionButton — one pattern for every button that creates, sends,
 * issues or records something.
 *
 * THE BUG THIS EXISTS FOR: two quick clicks on "Add payment" recorded two
 * payments. A `busy` state alone does not stop that reliably — the second click
 * can land before React has re-rendered the button disabled, and several
 * confirm buttons were never tied to a busy flag at all. So:
 *
 *   1. A REF guards the action synchronously: while one run is in flight, every
 *      further call returns immediately, whatever the button looks like.
 *   2. `pending` drives the disabled state and the "Saving…" label.
 *   3. An IDEMPOTENCY KEY per intent. The key is made once and reused until the
 *      action succeeds, so a retry after a network failure carries the same key
 *      and the server (middleware/idempotency.ts) returns the original record
 *      instead of creating a second one. After a success the next run gets a
 *      fresh key — a deliberate second payment is a new intent.
 *
 * Usage:
 *   const save = useAction();
 *   <button disabled={save.pending} onClick={() => save.run((key) => api.withIdempotencyKey(key).addPayment(…))}>
 *     {save.pending ? 'Saving…' : 'Add payment'}
 *   </button>
 * or simply <ActionButton onAction={(key) => …} pendingLabel="Saving…">Add payment</ActionButton>.
 *
 * run() rethrows the action's error (the caller shows it) and returns undefined
 * when it was ignored because a run was already in flight.
 */

import React, { useCallback, useRef, useState } from 'react';

export const newIdempotencyKey = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `k-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;

export interface Action {
  /** Run once; ignored (returns undefined) while a run is in flight. Errors propagate. */
  run: <T>(fn: (idempotencyKey: string) => Promise<T>) => Promise<T | undefined>;
  pending: boolean;
}

export function useAction(): Action {
  const inFlight = useRef(false);
  const key = useRef<string>(newIdempotencyKey());
  const [pending, setPending] = useState(false);

  const run = useCallback(async <T,>(fn: (idempotencyKey: string) => Promise<T>): Promise<T | undefined> => {
    if (inFlight.current) return undefined;
    inFlight.current = true;
    setPending(true);
    try {
      const out = await fn(key.current);
      key.current = newIdempotencyKey(); // the intent is done; the next click is a new one
      return out;
    } finally {
      inFlight.current = false;
      setPending(false);
    }
  }, []);

  return { run, pending };
}

/**
 * One key for the life of a component — for a modal that closes itself on save
 * and hands the work to its parent: one opening of the modal is one intent.
 */
export function useIntentKey(): string {
  const [key] = useState(newIdempotencyKey);
  return key;
}

type ButtonProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> & {
  onAction: (idempotencyKey: string) => Promise<unknown>;
  /** Label while the request is in flight. */
  pendingLabel?: React.ReactNode;
  /** Called with the error if the action throws (otherwise it is logged). */
  onError?: (e: any) => void;
};

/** A button that runs one action at a time and says so. */
export function ActionButton({ onAction, pendingLabel = 'Working…', onError, disabled, children, style, ...rest }: ButtonProps) {
  const a = useAction();
  return (
    <button
      {...rest}
      disabled={disabled || a.pending}
      aria-busy={a.pending || undefined}
      style={{ ...style, ...(a.pending ? { opacity: 0.6, cursor: 'progress' } : null) }}
      onClick={() => {
        a.run(onAction).catch((e) => (onError ? onError(e) : console.error('Action failed:', e)));
      }}
    >
      {a.pending ? pendingLabel : children}
    </button>
  );
}
